import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Checkbox } from "../ui/checkbox";
import { Layers, MapPinOff, X } from "lucide-react";
import { FASCE_LABEL } from "../../lib/constants";
import { makeNumberedIcon, depositoIcon } from "./leafletIcons";

// Fix default Leaflet icon (idempotente)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export function GiroMap({ data, filteredMezzi, visibleMezzi, setVisibleMezzi, activeTab }) {
  const [showNoCoordList, setShowNoCoordList] = useState(false);
  if (!data) return null;
  // B5: fallback difensivo — se non vengono passati filtri (es. usato dal giro operativo
  // da zone), usa tutti i mezzi di data come visibili.
  const mezziToRender = filteredMezzi ?? data.mezzi ?? [];
  const isVisible = (giroId, mezzoId) => {
    if (!visibleMezzi) return true;
    if (mezzoId && visibleMezzi[mezzoId] !== undefined) return visibleMezzi[mezzoId];
    if (visibleMezzi[giroId] !== undefined) return visibleMezzi[giroId];
    return true;
  };

  // Deposito mostrato SOLO se realmente configurato (coordinate precise in .env).
  const showDeposito = !!data.deposito?.configurato;

  // Punti visibili (fermate con coordinate) + elenco fermate senza coordinate
  // con il loro numero REALE nell'ordine del giro (solo lettura, nessuna modifica).
  const visiblePts = [];
  const senzaCoordList = [];
  const multiGiri = mezziToRender.filter((m) => isVisible(m.giroId, m.mezzo?.id)).length > 1;
  for (const m of mezziToRender) {
    if (!isVisible(m.giroId, m.mezzo?.id)) continue;
    m.fermate.forEach((s, i) => {
      if (s.lat && s.lng) visiblePts.push([s.lat, s.lng]);
      else senzaCoordList.push({ id: s.id, num: i + 1, ragioneSociale: s.ragioneSociale, indirizzo: s.indirizzo, zona: m.zona });
    });
  }
  const senzaCoordinate = senzaCoordList.length;
  const boundsPts = showDeposito
    ? [...visiblePts, [data.deposito.lat, data.deposito.lng]]
    : visiblePts;
  const mapProps = boundsPts.length
    ? { bounds: boundsPts, boundsOptions: { padding: [35, 35] } }
    : { center: [43.78, 11.24], zoom: 10 };

  return (
    <div className="h-[300px] sm:h-[420px] relative">
      <MapContainer {...mapProps} style={{ height: "100%", width: "100%", zIndex: 39 }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {showDeposito && (
          <Marker position={[data.deposito.lat, data.deposito.lng]} icon={depositoIcon()}>
            <Popup>{data.deposito.nome}</Popup>
          </Marker>
        )}
        {mezziToRender.map((m) => {
          if (!isVisible(m.giroId, m.mezzo?.id)) return null;
          const color = m.mezzo?.colore || "#9E9E9E";
          return (
            <React.Fragment key={m.giroId}>
              {m.fermate.map((s, i) => s.lat && s.lng && (
                <Marker key={s.id} position={[s.lat, s.lng]} icon={makeNumberedIcon(color, i + 1)}>
                  <Popup>
                    <div className="font-semibold">{s.ragioneSociale}</div>
                    <div className="text-xs text-slate-600">{s.indirizzo}</div>
                    <div className="text-xs text-slate-500">{FASCE_LABEL[s.fasciaOraria] || "-"} · {s.orarioRitiro}</div>
                  </Popup>
                </Marker>
              ))}
            </React.Fragment>
          );
        })}
      </MapContainer>
      {senzaCoordinate > 0 && (
        <button
          type="button"
          onClick={() => setShowNoCoordList((v) => !v)}
          className="absolute bottom-2 left-2 bg-amber-50 border border-amber-300 text-amber-900 rounded-lg shadow-md px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 z-[1000] cursor-pointer hover:bg-amber-100"
          data-testid="map-no-coord-banner"
          title="Clicca per vedere l'elenco delle fermate senza coordinate"
        >
          <MapPinOff size={14} className="shrink-0" />
          {senzaCoordinate} fermat{senzaCoordinate === 1 ? "a" : "e"} senza coordinate (non sulla mappa)
        </button>
      )}
      {senzaCoordinate > 0 && showNoCoordList && (
        <div
          className="absolute bottom-11 left-2 bg-white border border-amber-300 rounded-lg shadow-lg z-[1001] w-[300px] max-h-[220px] overflow-y-auto"
          data-testid="map-no-coord-list"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-amber-200 bg-amber-50 rounded-t-lg sticky top-0">
            <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900">Fermate senza coordinate</span>
            <button
              type="button"
              onClick={() => setShowNoCoordList(false)}
              className="text-amber-900 hover:text-amber-700"
              aria-label="Chiudi elenco"
              data-testid="map-no-coord-list-close"
            >
              <X size={14} />
            </button>
          </div>
          {senzaCoordList.map((s) => (
            <div key={s.id} className="flex items-start gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={`map-no-coord-item-${s.id}`}>
              <span className="h-5 w-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                {s.num}
              </span>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-900 truncate">{s.ragioneSociale}</div>
                <div className="text-[11px] text-slate-500 truncate">{s.indirizzo || "—"}</div>
                {multiGiri && s.zona && <div className="text-[10px] text-slate-400 truncate">{s.zona}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {activeTab === "ALL" && (
        <div className="absolute top-2 right-2 bg-white rounded-lg border border-slate-200 shadow-md p-2 space-y-1.5 z-[1000] max-w-[260px]" data-testid="map-legend">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 px-1">
            <Layers size={12} /> Mezzi attivi oggi
          </div>
          {data.mezzi.filter((m) => m.mezzo).map((m) => {
            const noCoord = m.fermate.filter((s) => !(s.lat && s.lng)).length;
            return (
              <label key={m.mezzo.id} className="flex items-start gap-2 text-xs cursor-pointer px-1.5 py-1.5 hover:bg-slate-50 rounded border border-transparent hover:border-slate-100">
                <Checkbox
                  checked={!!visibleMezzi[m.mezzo.id]}
                  onCheckedChange={(v) => setVisibleMezzi({ ...visibleMezzi, [m.mezzo.id]: !!v })}
                  data-testid={`toggle-${m.mezzo.id}`}
                  className="mt-0.5"
                />
                <span className="h-3 w-3 rounded-full shrink-0 mt-1" style={{ backgroundColor: m.mezzo.colore }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 truncate text-[11px]">{m.mezzo.nome}</div>
                  <div className="text-[10px] text-slate-500 truncate">{m.zona}</div>
                  <div className="text-[10px] text-slate-600 mt-0.5">
                    <span className="font-medium">{m.fermate.length}</span> tappe
                    {noCoord > 0 && (
                      <span className="text-amber-700 font-semibold ml-1">· {noCoord} senza coordinate</span>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
